import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import type { TFunction } from 'i18next'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../../utils/echarts'
import { Icon } from '../../../../../atoms/Icon'
import { IconButton } from '../../../../../atoms/IconButton'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import { Badge } from '../../../../../atoms/Badge'
import { downloadChartAsPng, sanitizeFilename } from '../../../../../../utils'
import {
  Grid,
  Card,
  CardHeader,
  CardTitle,
  ChartContainer,
} from '../../../BuildingCharacteristics.styles'
import { CHART_COLORS } from '../NeighborhoodContextTab.constants'

export interface ComparisonChartsSectionRef {
  exportHeightChart: () => string | null
  exportAreaChart: () => string | null
  exportVolumeChart: () => string | null
}

interface ComparisonChartsSectionProps {
  buildingHeight: number
  buildingArea: number
  buildingVolume: number
  neighborsHeightMean: number
  neighborsHeightMax: number
  neighborsAreaMean: number
  neighborsAreaMax: number
  neighborsVolumeMean: number
  neighborsVolumeMax: number
  neighborsCount: number
  t: TFunction
}

export const ComparisonChartsSection = forwardRef<
  ComparisonChartsSectionRef,
  ComparisonChartsSectionProps
>(
  (
    {
      buildingHeight,
      buildingArea,
      buildingVolume,
      neighborsHeightMean,
      neighborsHeightMax,
      neighborsAreaMean,
      neighborsAreaMax,
      neighborsVolumeMean,
      neighborsVolumeMax,
      neighborsCount,
      t,
    },
    ref
  ) => {
    const heightChartRef = useRef<HTMLDivElement>(null)
    const areaChartRef = useRef<HTMLDivElement>(null)
    const volumeChartRef = useRef<HTMLDivElement>(null)
    const heightChartInstance = useRef<echarts.ECharts | null>(null)
    const areaChartInstance = useRef<echarts.ECharts | null>(null)
    const volumeChartInstance = useRef<echarts.ECharts | null>(null)

    useImperativeHandle(ref, () => ({
      exportHeightChart: () => {
        if (heightChartInstance.current) {
          return heightChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
      exportAreaChart: () => {
        if (areaChartInstance.current) {
          return areaChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
      exportVolumeChart: () => {
        if (volumeChartInstance.current) {
          return volumeChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
    }))

    // Height comparison chart
    useEffect(() => {
      if (
        heightChartRef.current &&
        buildingHeight > 0 &&
        neighborsHeightMean > 0
      ) {
        const chart = safeChartInit(heightChartRef.current)
        heightChartInstance.current = chart
        const option = {
          animation: true,
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
          },
          grid: {
            left: '15%',
            right: '10%',
            bottom: '10%',
            top: '5%',
          },
          xAxis: {
            type: 'value',
            name: t('neighborhood.chart.heightAxis'),
          },
          yAxis: {
            type: 'category',
            data: [
              t('neighborhood.chart.thisBuilding'),
              t('neighborhood.chart.neighborhoodAvg'),
              t('neighborhood.chart.neighborhoodMax'),
            ],
          },
          series: [
            {
              type: 'bar',
              data: [
                {
                  value: buildingHeight,
                  itemStyle: { color: CHART_COLORS.BUILDING },
                },
                {
                  value: neighborsHeightMean,
                  itemStyle: { color: CHART_COLORS.NEIGHBORHOOD_AVG },
                },
                {
                  value: neighborsHeightMax,
                  itemStyle: { color: CHART_COLORS.NEIGHBORHOOD_MAX },
                },
              ],
              label: {
                show: true,
                position: 'right',
                formatter: '{c} m',
              },
            },
          ],
        }
        chart.setOption(option)

        const handleResize = () => chart.resize()
        window.addEventListener('resize', handleResize)
        return () => {
          window.removeEventListener('resize', handleResize)
          chart.dispose()
          heightChartInstance.current = null
        }
      }
    }, [buildingHeight, neighborsHeightMean, neighborsHeightMax, t])

    // Area comparison chart
    useEffect(() => {
      if (areaChartRef.current && buildingArea > 0 && neighborsAreaMean > 0) {
        const chart = safeChartInit(areaChartRef.current)
        areaChartInstance.current = chart
        const option = {
          animation: true,
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
          },
          grid: {
            left: '15%',
            right: '10%',
            bottom: '10%',
            top: '5%',
          },
          xAxis: {
            type: 'value',
            name: t('neighborhood.chart.areaAxis'),
          },
          yAxis: {
            type: 'category',
            data: [
              t('neighborhood.chart.thisBuilding'),
              t('neighborhood.chart.neighborhoodAvg'),
              t('neighborhood.chart.neighborhoodMax'),
            ],
          },
          series: [
            {
              type: 'bar',
              data: [
                {
                  value: buildingArea,
                  itemStyle: { color: CHART_COLORS.BUILDING },
                },
                {
                  value: neighborsAreaMean,
                  itemStyle: { color: CHART_COLORS.NEIGHBORHOOD_AVG },
                },
                {
                  value: neighborsAreaMax,
                  itemStyle: { color: CHART_COLORS.NEIGHBORHOOD_MAX },
                },
              ],
              label: {
                show: true,
                position: 'right',
                formatter: '{c} m²',
              },
            },
          ],
        }
        chart.setOption(option)

        const handleResize = () => chart.resize()
        window.addEventListener('resize', handleResize)
        return () => {
          window.removeEventListener('resize', handleResize)
          chart.dispose()
          areaChartInstance.current = null
        }
      }
    }, [buildingArea, neighborsAreaMean, neighborsAreaMax, t])

    // Volume comparison chart
    useEffect(() => {
      if (
        volumeChartRef.current &&
        buildingVolume > 0 &&
        neighborsVolumeMean > 0
      ) {
        const chart = safeChartInit(volumeChartRef.current)
        volumeChartInstance.current = chart
        const option = {
          animation: true,
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
          },
          grid: {
            left: '15%',
            right: '10%',
            bottom: '10%',
            top: '5%',
          },
          xAxis: {
            type: 'value',
            name: t('neighborhood.chart.volumeAxis'),
          },
          yAxis: {
            type: 'category',
            data: [
              t('neighborhood.chart.thisBuilding'),
              t('neighborhood.chart.neighborhoodAvg'),
              t('neighborhood.chart.neighborhoodMax'),
            ],
          },
          series: [
            {
              type: 'bar',
              data: [
                {
                  value: buildingVolume,
                  itemStyle: { color: CHART_COLORS.BUILDING },
                },
                {
                  value: neighborsVolumeMean,
                  itemStyle: { color: CHART_COLORS.NEIGHBORHOOD_AVG },
                },
                {
                  value: neighborsVolumeMax,
                  itemStyle: { color: CHART_COLORS.NEIGHBORHOOD_MAX },
                },
              ],
              label: {
                show: true,
                position: 'right',
                formatter: (params: any) =>
                  `${Math.round(params.value).toLocaleString()} m³`,
              },
            },
          ],
        }
        chart.setOption(option)

        const handleResize = () => chart.resize()
        window.addEventListener('resize', handleResize)
        return () => {
          window.removeEventListener('resize', handleResize)
          chart.dispose()
          volumeChartInstance.current = null
        }
      }
    }, [buildingVolume, neighborsVolumeMean, neighborsVolumeMax, t])

    const handleDownloadHeight = () => {
      const dataUrl = heightChartInstance.current?.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff',
      })
      downloadChartAsPng(
        dataUrl || null,
        sanitizeFilename(t('neighborhood.cards.heightComparison.title'))
      )
    }

    const handleDownloadArea = () => {
      const dataUrl = areaChartInstance.current?.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff',
      })
      downloadChartAsPng(
        dataUrl || null,
        sanitizeFilename(t('neighborhood.cards.areaComparison.title'))
      )
    }

    const handleDownloadVolume = () => {
      const dataUrl = volumeChartInstance.current?.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff',
      })
      downloadChartAsPng(
        dataUrl || null,
        sanitizeFilename(t('neighborhood.cards.volumeComparison.title'))
      )
    }

    return (
      <Grid $columns={1}>
        {/* Height Comparison */}
        {buildingHeight > 0 && neighborsHeightMean > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                <Icon name="arrow-up" size="small" />
                {t('neighborhood.cards.heightComparison.title')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'neighborhood.cards.heightComparison.tooltip.title'
                    )}
                    shortDescription={t(
                      'neighborhood.cards.heightComparison.tooltip.description'
                    )}
                    calculation={t(
                      'neighborhood.cards.heightComparison.tooltip.calculation'
                    )}
                    importance={t(
                      'neighborhood.cards.heightComparison.tooltip.importance'
                    )}
                    limitations={t(
                      'neighborhood.cards.heightComparison.tooltip.limitations'
                    )}
                    source={t('neighborhood.charts.height.source')}
                  />
                </InfoTooltip>
              </CardTitle>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Badge variant="info">
                  {t('neighborhood.neighboringBuildings', {
                    count: neighborsCount,
                  })}
                </Badge>
                <IconButton
                  size="small"
                  variant="secondary"
                  onClick={handleDownloadHeight}
                  aria-label={t('common.downloadChart')}
                  title={t('common.downloadChart')}
                >
                  <Icon name="download" size="small" />
                </IconButton>
              </div>
            </CardHeader>
            <ChartContainer ref={heightChartRef} style={{ height: '200px' }} />
          </Card>
        )}

        {/* Area Comparison */}
        {buildingArea > 0 && neighborsAreaMean > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                <Icon name="square" size="small" />
                {t('neighborhood.cards.areaComparison.title')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t('neighborhood.cards.areaComparison.tooltip.title')}
                    shortDescription={t(
                      'neighborhood.cards.areaComparison.tooltip.description'
                    )}
                    calculation={t(
                      'neighborhood.cards.areaComparison.tooltip.calculation'
                    )}
                    importance={t(
                      'neighborhood.cards.areaComparison.tooltip.importance'
                    )}
                    limitations={t(
                      'neighborhood.cards.areaComparison.tooltip.limitations'
                    )}
                    source={t('neighborhood.charts.area.source')}
                  />
                </InfoTooltip>
              </CardTitle>
              <IconButton
                size="small"
                variant="secondary"
                onClick={handleDownloadArea}
                aria-label={t('common.downloadChart')}
                title={t('common.downloadChart')}
              >
                <Icon name="download" size="small" />
              </IconButton>
            </CardHeader>
            <ChartContainer ref={areaChartRef} style={{ height: '200px' }} />
          </Card>
        )}

        {/* Volume Comparison */}
        {buildingVolume > 0 && neighborsVolumeMean > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                <Icon name="box" size="small" />
                {t('neighborhood.cards.volumeComparison.title')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'neighborhood.cards.volumeComparison.tooltip.title'
                    )}
                    shortDescription={t(
                      'neighborhood.cards.volumeComparison.tooltip.description'
                    )}
                    calculation={t(
                      'neighborhood.cards.volumeComparison.tooltip.calculation'
                    )}
                    importance={t(
                      'neighborhood.cards.volumeComparison.tooltip.importance'
                    )}
                    limitations={t(
                      'neighborhood.cards.volumeComparison.tooltip.limitations'
                    )}
                    source={t('neighborhood.charts.volume.source')}
                  />
                </InfoTooltip>
              </CardTitle>
              <IconButton
                size="small"
                variant="secondary"
                onClick={handleDownloadVolume}
                aria-label={t('common.downloadChart')}
                title={t('common.downloadChart')}
              >
                <Icon name="download" size="small" />
              </IconButton>
            </CardHeader>
            <ChartContainer ref={volumeChartRef} style={{ height: '200px' }} />
          </Card>
        )}
      </Grid>
    )
  }
)

ComparisonChartsSection.displayName = 'ComparisonChartsSection'
